import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { light } from '~/constants/theme'
import { TranscriptionLogContextProvider } from '~/pages/CallAnalytics/Transcription/context/TranscriptionLogContext'
import TranscriptionEntry from '~/pages/CallAnalytics/Transcription/TranscriptionEntry'
import TranscriptLogPause from '~/pages/CallAnalytics/Transcription/TranscriptLogPause'
import { CallTranscript, TranscriptEntry } from '~/types/CallTranscript'

const TranscriptionLogWrapper = styled.div`
  display: flex;
  flex: 1;
  -webkit-box-flex: 1;
  background: ${light.gray100};
  overflow: auto;
`
const TranscriptionEntriesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  -webkit-box-flex: 1;
  position: relative;
`

interface TranscriptionLogProps {
  transcriptionData: CallTranscript
  isPlaying: boolean
  playerPosition: number
  onPlay: (time?: number) => void
  onPause: () => void
}

const TranscriptionLog = ({
  transcriptionData,
  isPlaying,
  playerPosition,
  onPlay,
  onPause,
}: TranscriptionLogProps) => {
  const tEntryRefs = useRef<HTMLDivElement[]>([])
  const [activeEntryIndices, setActiveEntryIndices] = useState<number[]>([])
  const getActiveTranscriptionEntries = () => {
    const tEntryIndices: number[] = []
    if (playerPosition < transcriptionData.Transcript[0].BeginOffsetMillis) {
      tEntryIndices.push(0)
      return tEntryIndices
    }

    let priorEntry: TranscriptEntry | null = null
    for (let i = 0; i < transcriptionData.Transcript.length; i++) {
      const entry = transcriptionData.Transcript[i]
      if (
        (entry.BeginOffsetMillis <= playerPosition &&
          entry.EndOffsetMillis >= playerPosition) ||
        //if the previously tested entry ends after this one, but this one did not satisfy the requirements of having a begin/end between the current scrubber position, add it anyway
        (priorEntry && priorEntry.EndOffsetMillis > entry.EndOffsetMillis)
      ) {
        tEntryIndices.push(i)
        priorEntry = entry
      }
    }

    if (
      tEntryIndices.length === 0 &&
      playerPosition >
        transcriptionData.Transcript[transcriptionData.Transcript.length - 1]
          .EndOffsetMillis
    ) {
      tEntryIndices.push(transcriptionData.Transcript.length - 1)
    }

    return tEntryIndices
  }

  const currentScrollIndex = useRef(-1)
  //calculates the auto-scroll behavior based on the active entries
  useEffect(() => {
    const tEntryIndices = getActiveTranscriptionEntries()
    if (tEntryIndices.length <= 0) return
    const activeRefIndex = tEntryIndices[tEntryIndices.length - 1]

    if (currentScrollIndex.current === activeRefIndex) {
      return
    }

    setActiveEntryIndices([...tEntryIndices])
    const indexDelta = Math.abs(currentScrollIndex.current - activeRefIndex)
    const scrollDeltaMax = 2
    currentScrollIndex.current = activeRefIndex
    const entryRef = tEntryRefs.current[activeRefIndex]
    const behavior = indexDelta > scrollDeltaMax ? 'auto' : 'smooth'
    entryRef.scrollIntoView({ behavior: behavior, block: 'center' })
  }, [playerPosition])

  const setChildRefIndex = (index: number, childRef: HTMLDivElement | null) => {
    if (!childRef) return
    tEntryRefs.current[index] = childRef
  }

  const entriesJsx = useMemo(() => {
    const entries: JSX.Element[] = []
    for (let i = 0; i < transcriptionData.Transcript.length; i++) {
      const transcriptEntry = transcriptionData.Transcript[i]
      const isActive = activeEntryIndices.includes(i)
      const nextEntry =
        i < transcriptionData.Transcript.length - 1
          ? transcriptionData.Transcript[i + 1]
          : null

      const pauseStartMillis = transcriptEntry.EndOffsetMillis
      const pauseEndMillis = nextEntry?.BeginOffsetMillis

      entries.push(
        <div key={i}>
          <TranscriptionEntry
            index={i}
            transcriptName={transcriptionData.JobName}
            childRefFunc={(childRef) => setChildRefIndex(i, childRef)}
            transcriptEntry={transcriptEntry}
            isActive={isActive}
            isWaiting={Math.max(...activeEntryIndices) < i}
            isPlaying={isPlaying}
          />
          <TranscriptLogPause
            pauseStartTimestampMillis={pauseStartMillis}
            pauseEndTimestampMillis={pauseEndMillis}
          />
        </div>,
      )
    }
    return entries
  }, [transcriptionData, activeEntryIndices, isPlaying])

  const contextObj = {
    elapsedTimeMillis: playerPosition,
    isPlaying,
    playerPosition,
    onPlay,
    onPause,
  }

  return (
    <TranscriptionLogWrapper>
      <TranscriptionEntriesWrapper>
        <TranscriptionLogContextProvider value={contextObj}>
          {entriesJsx}
        </TranscriptionLogContextProvider>
      </TranscriptionEntriesWrapper>
    </TranscriptionLogWrapper>
  )
}

export default TranscriptionLog
